import type { FC } from 'react';
import React from 'react';

import { ClickableDiv } from '@wwe-portal/ui/components/button';
import type { PlayerRosterForClubStore } from '@wwe-portal/entities/roster.type';
import { cn } from '@wwe-portal/ui/lib/lib';
import upgradePlayerIcon from '@wwe-portal/ui/assets/images/subscription/upgrade-player.svg';

import { SelectableRosterStar } from '../roster-star';

import { TalentBadge } from './power-badge';

export interface PlayerCardProps {
  data: PlayerRosterForClubStore;
  hasUpgradeIcon?: boolean;
  onClick: () => void;
}

export const PlayerCardMobile: FC<PlayerCardProps> = ({
  data: { id, name, price, level, star, img, hollowStar = 0, disabled },
  hasUpgradeIcon,
  onClick,
}) => {
  return (
    <ClickableDiv
      className={cn(
        'font-poppins bg-back300 flex h-full w-full flex-row items-stretch justify-between gap-4 rounded-[10px] p-[10px] pr-5',
        { 'pointer-events-none grayscale': disabled }
      )}
      data-roster-id={id}
      onClick={onClick}
    >
      <div className="flex w-full items-center justify-between gap-3">
        <div className="relative h-[55px] w-[48px]">
          <img
            alt="player card"
            className="absolute inset-y-0 right-0 h-full w-[42px]"
            src={img}
          />
          {hasUpgradeIcon && (
            <img
              alt="upgrade icon"
              className="absolute bottom-0 left-0"
              src={upgradePlayerIcon}
              width={14}
            />
          )}
        </div>
        <div className="w-0 grow">
          <div className="text-text300 w-full overflow-hidden text-ellipsis whitespace-nowrap text-xs leading-[18px]">
            {name}
          </div>
          <div className="text-yellow800 pb-[10px] pt-0 font-[12px] leading-[18px]">
            {price}
          </div>
          <div>
            {level != null && star != null && (
              <SelectableRosterStar
                className="gap-1"
                hollowStar={hollowStar}
                level={level}
                maxRate={star + hollowStar}
                rate={star}
                selected={0}
                size={10}
              />
            )}
          </div>
        </div>
      </div>
    </ClickableDiv>
  );
};

export const PlayerCardDesktop: FC<PlayerCardProps> = ({
  data: {
    id,
    title,
    description,
    price,
    level,
    star,
    img,
    hollowStar,
    disabled,
  },
  onClick,
}) => {
  return (
    <ClickableDiv
      className={cn(
        'font-poppins bg-back300 flex h-full w-full flex-col items-center justify-center rounded-[10px] px-3 pb-5 pt-2',
        { 'pointer-events-none grayscale': disabled }
      )}
      data-roster-id={id}
      onClick={onClick}
    >
      <div className="h-0 flex-grow pb-1 pt-6">
        <img
          alt="player card v1"
          className="h-full w-[196px] object-contain"
          src={img}
        />
      </div>
      <div className="flex h-[126px] flex-col items-center gap-3">
        <div className="flex flex-col items-center">
          <span className="text-text300 text-text500 line-clamp-1 text-base font-normal leading-6">
            {title}
          </span>
          <span className="text-text400 text-text600 line-clamp-1 text-xs font-normal leading-5">
            {description}
          </span>
        </div>
        {price && (
          <div className="h-8">
            <TalentBadge value={price} />
          </div>
        )}
        <div>
          {level != null && star != null && (
            <SelectableRosterStar
              className="gap-1 drop-shadow-[1px_1px_2px_rgba(0,0,0,0.35)]"
              hollowStar={hollowStar}
              level={level}
              maxRate={star + hollowStar}
              rate={star}
              selected={0}
              size={11}
            />
          )}
        </div>
      </div>
    </ClickableDiv>
  );
};
